/* eslint-disable max-len, react/jsx-closing-tag-location, react/jsx-indent, react/jsx-wrap-multilines */
import React, { useEffect, useState } from 'react';
import {
  Alert, Badge, Button, Card, Form, Spinner,
} from '@openedx/paragon';
import { useIntl } from '@edx/frontend-platform/i18n';

import {
  cancelSkillBuilderJob,
  countSkillBuilderUnitSourceChunks,
  createSkillBuilderSourceSession,
  getSkillBuilderJob,
  getSkillBuilderJobResult,
  listSkillBuilderSourceSessionSources,
  searchSkillBuilderUnitSources,
  startSkillBuilderJob,
  UnitBuilderSourceResult,
  uploadSkillBuilderSource,
} from '../data/api';
import useSkillBuilderJob from '../hooks/useSkillBuilderJob';
import messages from '../messages';
import JobStatus from './JobStatus';
import copyTextToClipboard from '../utils/clipboard';

const STEPS = ['Topic', 'Sources', 'Template', 'Build'];
const TEMPLATES = [
  {
    id: 'content_standard',
    label: 'Standard',
    tagline: 'General-purpose concept unit',
    description: 'Clear definition, practical application, and a grounding example.',
    structure: ['Lead', 'Core Concept', 'Application', 'Key Insight', 'Example', 'Reflection', 'Takeaway'],
  },
  {
    id: 'content_framework',
    label: 'Framework',
    tagline: 'Named model or multi-step method',
    description: 'A named model, method, or framework with distinct components.',
    structure: ['Lead', 'Framework', 'Component breakdown', 'Example', 'Reflection', 'Takeaway'],
  },
  {
    id: 'content_data_driven',
    label: 'Data-Driven',
    tagline: 'Research finding or statistic as the anchor',
    description: 'An empirical finding or research-backed statistic is central to the unit.',
    structure: ['Lead', 'Core concept', 'The Research', 'Study Details', 'Takeaway'],
  },
  {
    id: 'content_case_study',
    label: 'Case Study',
    tagline: 'Judgment and interpersonal-skill scenario',
    description: 'A scenario-led unit for nuanced decisions and applied professional judgment.',
    structure: ['Lead', 'Concept', 'The Situation', 'Decision Point', 'Takeaway'],
  },
];
const SOURCE_ACCEPT = '.pdf,.docx,.txt';
const PRIMARY_CHUNK_CAP = 20;
const FULL_CONTEXT_CHUNK_CAP = 45;
const WORDS_PER_CHUNK = 100;

interface AddedSource {
  id: string;
  type: 'edx_url' | 'edx_library' | 'web';
  label: string;
  verticalId?: string;
  courseId?: string;
  title?: string;
  url?: string;
  snippet?: string;
  score?: number;
}

interface UploadedSource {
  sourceId: string;
  name: string;
  jobId: string;
  status: string;
  includedWords?: number;
}

interface UnitResult {
  html: string;
  sources_cited: string[];
  concepts_taught: string[];
  template_name: string;
}

const parseEdxUnitUrl = (url: string) => {
  const course = url.match(/(?:^|\/)(course-v1:[^/?#]+)/i);
  const sequential = url.match(/type@sequential\+block@([a-f0-9]+)/i);
  const vertical = url.match(/type@vertical\+block@([a-f0-9]+)/i);
  return course && sequential && vertical ? {
    courseId: decodeURIComponent(course[1]), sequentialId: sequential[1], verticalId: vertical[1],
  } : null;
};

const isWaiting = (status: string) => ['queued', 'running', 'cancel_requested'].includes(status);

const sourceStatusVariant = (status: string) => {
  if (status === 'completed') { return 'success'; }
  if (status === 'failed') { return 'danger'; }
  return 'warning';
};

const countHtmlWords = (html: string) => {
  const container = document.createElement('div');
  container.innerHTML = html;
  container.querySelectorAll('style, script, summary').forEach(element => element.remove());
  let text = '';
  container.querySelectorAll('p, div').forEach(element => {
    const clone = element.cloneNode(true) as HTMLElement;
    clone.querySelectorAll('p, div').forEach(child => child.remove());
    text += ` ${clone.textContent || ''}`;
  });
  return text.trim().split(/\s+/).filter(Boolean).length;
};

const SingleUnitTab: React.FC = () => {
  const intl = useIntl();
  const [activeStep, setActiveStep] = useState(0);
  const [topic, setTopic] = useState('');
  const [wordCount, setWordCount] = useState(450);
  const [instructions, setInstructions] = useState('');
  const [sources, setSources] = useState<AddedSource[]>([]);
  const [uploadedSources, setUploadedSources] = useState<UploadedSource[]>([]);
  const [sourceSessionId, setSourceSessionId] = useState<string | null>(null);
  const [templateId, setTemplateId] = useState('');
  const [fullContextMode, setFullContextMode] = useState(false);
  const [chunkCounts, setChunkCounts] = useState<Record<string, number>>({});
  const [edxUrl, setEdxUrl] = useState('');
  const [sourceError, setSourceError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [useWeb, setUseWeb] = useState(true);
  const [useEdx, setUseEdx] = useState(true);
  const [searchResults, setSearchResults] = useState<UnitBuilderSourceResult[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [result, setResult] = useState<UnitResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const job = useSkillBuilderJob(jobId);

  const primarySource = sources.find(source => source.type === 'edx_url');
  const readySourceIds = uploadedSources.filter(source => source.status === 'completed').map(source => source.sourceId);
  const sourceProcessing = uploadedSources.some(source => isWaiting(source.status));
  const searchDisabled = fullContextMode && Boolean(primarySource);
  const sourceWords = sources.reduce((total, source) => {
    if (source.type === 'web') { return total + (source.snippet?.split(/\s+/).length || 80); }
    if (source.type === 'edx_library') { return total + 150; }
    if (source.verticalId) {
      const cap = fullContextMode && source.type === 'edx_url' ? FULL_CONTEXT_CHUNK_CAP : PRIMARY_CHUNK_CAP;
      return total + Math.min(chunkCounts[source.verticalId] || cap, cap) * WORDS_PER_CHUNK;
    }
    return total;
  }, uploadedSources.reduce((total, source) => total + (source.includedWords || 0), 0));

  useEffect(() => {
    const primarySources = sources.flatMap(source => (
      source.type === 'edx_url' && source.courseId && source.verticalId
        ? [{ course_id: source.courseId, vertical_id: source.verticalId }]
        : []
    ));
    if (!primarySources.length) {
      setChunkCounts({});
      if (fullContextMode) { setFullContextMode(false); }
      return undefined;
    }
    countSkillBuilderUnitSourceChunks(primarySources)
      .then(data => setChunkCounts(data.counts))
      .catch(() => setChunkCounts({}));
    return undefined;
  }, [fullContextMode, sources]);

  useEffect(() => {
    if (!uploadedSources.some(source => isWaiting(source.status))) { return undefined; }
    let cancelled = false;
    const refresh = async () => {
      const updates = await Promise.all(uploadedSources.filter(source => isWaiting(source.status)).map(async source => {
        const latest = await getSkillBuilderJob(source.jobId);
        return { sourceId: source.sourceId, status: latest.status };
      }));
      if (cancelled) { return; }
      setUploadedSources(current => current.map(source => {
        const update = updates.find(item => item.sourceId === source.sourceId);
        return update ? { ...source, status: update.status } : source;
      }));
      if (sourceSessionId && updates.some(update => update.status === 'completed')) {
        const sourceMetadata = await listSkillBuilderSourceSessionSources(sourceSessionId);
        if (!cancelled) {
          setUploadedSources(current => current.map(source => {
            const metadata = sourceMetadata.find(item => item.source_id === source.sourceId);
            return metadata?.extracted ? {
              ...source,
              name: metadata.filename || source.name,
              includedWords: metadata.included_words,
            } : source;
          }));
        }
      }
    };
    refresh().catch(() => setSourceError('Unable to retrieve source processing status.'));
    const intervalId = window.setInterval(() => {
      refresh().catch(() => setSourceError('Unable to retrieve source processing status.'));
    }, 3000);
    return () => { cancelled = true; window.clearInterval(intervalId); };
  }, [sourceSessionId, uploadedSources]);

  useEffect(() => {
    if (job.job?.status !== 'completed' || !jobId || result) { return; }
    getSkillBuilderJobResult(jobId)
      .then(data => setResult(data as UnitResult))
      .catch(() => setError(intl.formatMessage(messages.resultLoadError)));
  }, [intl, job.job?.status, jobId, result]);

  const reset = () => {
    setActiveStep(0);
    setTopic('');
    setWordCount(450);
    setInstructions('');
    setSources([]);
    setUploadedSources([]);
    setSourceSessionId(null);
    setTemplateId('');
    setFullContextMode(false);
    setSearchResults(null);
    setJobId(null);
    setResult(null);
    setError(null);
  };

  const addEdxUrl = () => {
    if (primarySource) {
      setSourceError('You can only reference one EdX unit URL. Remove the existing unit first.');
      return;
    }
    const parsed = parseEdxUnitUrl(edxUrl.trim());
    if (!parsed) {
      setSourceError('Paste the full learner-view unit URL with course, sequential, and vertical block IDs.');
      return;
    }
    setSources(current => [...current, {
      id: `edx-${parsed.sequentialId}-${parsed.verticalId}`,
      type: 'edx_url',
      label: `EdX unit - ${parsed.verticalId.slice(0, 8)}...`,
      courseId: parsed.courseId,
      verticalId: parsed.verticalId,
    }]);
    setEdxUrl('');
    setSourceError(null);
  };

  const uploadSources = async (files: FileList | null) => {
    if (!files?.length) { return; }
    setSourceError(null);
    setIsUploading(true);
    try {
      let sessionId = sourceSessionId;
      if (!sessionId) {
        const session = await createSkillBuilderSourceSession();
        sessionId = session.job_id;
        setSourceSessionId(sessionId);
      }
      const uploads = await Promise.all(Array.from(files).map(async file => {
        const upload = await uploadSkillBuilderSource(sessionId as string, file);
        return {
          sourceId: upload.source_id, name: file.name, jobId: upload.job_id, status: upload.status,
        };
      }));
      setUploadedSources(current => [...current, ...uploads]);
    } catch {
      setSourceError(intl.formatMessage(messages.sourceUploadError));
    } finally {
      setIsUploading(false);
    }
  };

  const searchSources = async (append = false) => {
    if (!topic.trim() || searchDisabled) { return; }
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsSearching(true);
    }
    try {
      const existingIds = append && searchResults ? searchResults.map(item => item.id) : [];
      const data = await searchSkillBuilderUnitSources({
        topic,
        useWeb,
        useEdx,
        excludeIds: existingIds,
      });
      setSearchResults(current => (append && current ? [...current, ...data.results] : data.results));
    } catch {
      setSourceError('Unable to search for sources. Try again.');
      if (!append) { setSearchResults([]); }
    } finally {
      setIsSearching(false);
      setIsLoadingMore(false);
    }
  };

  const resultId = (source: UnitBuilderSourceResult) => (
    source.type === 'edx_library' && source.vertical_id ? `vertical:${source.vertical_id}` : source.id
  );

  const toggleSearchResult = (source: UnitBuilderSourceResult) => {
    const id = resultId(source);
    setSources(current => (current.some(item => item.id === id)
      ? current.filter(item => item.id !== id)
      : [...current, {
        id,
        type: source.type,
        label: source.title || source.url || source.id,
        verticalId: source.vertical_id,
        title: source.title,
        url: source.url,
        snippet: source.snippet,
        score: source.score,
      }]));
  };

  const submit = async () => {
    setError(null);
    setResult(null);
    try {
      const started = await startSkillBuilderJob({
        stage: 'unit',
        input: {
          topic: topic.trim(),
          template_id: templateId,
          word_count: wordCount,
          instructions,
          primary_sources: sources
            .filter(source => source.type === 'edx_url' && source.courseId && source.verticalId)
            .map(source => ({ course_id: source.courseId, vertical_id: source.verticalId })),
          source_ids: sources.filter(source => source.type === 'edx_library').map(source => source.id),
          web_sources: sources.filter(source => source.type === 'web').map(source => ({
            id: source.id, title: source.title || '', url: source.url || '', snippet: source.snippet || '',
          })),
          full_context_mode: fullContextMode,
        },
        source_session_id: sourceSessionId || undefined,
        source_ids: readySourceIds,
      });
      setJobId(started.job_id);
    } catch {
      setError(intl.formatMessage(messages.jobSubmitError));
    }
  };

  const copyHtml = async () => {
    if (!result?.html) { return; }
    try {
      await copyTextToClipboard(result.html);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Unable to copy generated HTML. Select it from the preview and copy it manually.');
    }
  };

  const canContinue = () => {
    if (activeStep === 0) { return topic.trim().length >= 3; }
    if (activeStep === 2) { return Boolean(templateId); }
    return true;
  };
  const generationLabel = () => {
    if (job.job && !job.isTerminal) { return 'Generating...'; }
    if (result) { return 'Regenerate unit'; }
    return 'Generate unit';
  };
  const template = TEMPLATES.find(item => item.id === templateId);

  return (
    <section className="ai-assisted-course-authoring-tab__workspace" aria-labelledby="single-unit-heading">
      <h3 id="single-unit-heading">{intl.formatMessage(messages.singleUnitHeading)}</h3>
      <p>{intl.formatMessage(messages.singleUnitDescription)}</p>
      {error && <Alert variant="danger">{error}</Alert>}

      <nav aria-label="Unit Builder steps">
        <ol className="ai-assisted-course-authoring-tab__wizard-steps">
          {STEPS.map((label, index) => (
            <li key={label} className={index === activeStep ? 'is-active' : ''}>
              <Button variant="tertiary" size="sm" disabled={index > activeStep} aria-current={index === activeStep ? 'step' : undefined} onClick={() => setActiveStep(index)}>
                <span aria-hidden="true">{index + 1}. </span>{label}
              </Button>
            </li>
          ))}
        </ol>
      </nav>

      {activeStep === 0 && (
      <div className="ai-assisted-course-authoring-tab__unit-step">
        <Form.Group>
          <Form.Label htmlFor="ai-unit-topic">What is this unit about?</Form.Label>
          <Form.Control id="ai-unit-topic" maxLength={200} value={topic} placeholder="e.g. Active Listening in Remote Teams" onChange={(event: React.ChangeEvent<HTMLInputElement>) => setTopic(event.target.value)} />
          <Form.Text>{topic.length}/200 characters</Form.Text>
        </Form.Group>
        <Form.Group>
          <Form.Label htmlFor="ai-unit-word-count">Target word count: about {wordCount} words</Form.Label>
          <Form.Control id="ai-unit-word-count" type="range" min={150} max={800} step={50} value={wordCount} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setWordCount(Number(event.target.value))} />
          <Form.Text>450 words is a standard content unit. Use 600-800 for detailed, framework-heavy topics.</Form.Text>
        </Form.Group>
        <Form.Group>
          <Form.Label htmlFor="ai-unit-instructions">Additional instructions (optional)</Form.Label>
          <Form.Control id="ai-unit-instructions" as="textarea" rows={3} maxLength={500} value={instructions} placeholder="e.g. Include a customer-service scenario and avoid jargon." onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setInstructions(event.target.value)} />
          <Form.Text>{instructions.length}/500 characters</Form.Text>
        </Form.Group>
      </div>
      )}

      {activeStep === 1 && (
      <div className="ai-assisted-course-authoring-tab__unit-step">
        <Card><Card.Body className="ai-assisted-course-authoring-tab__card-body">
          <h4>Upload documents</h4><p>PDF, DOCX, and text files remain on the server and are only used for this generation job.</p>
          <Form.Control type="file" accept={SOURCE_ACCEPT} multiple disabled={isUploading} onChange={(event: React.ChangeEvent<HTMLInputElement>) => uploadSources(event.target.files)} />
          {isUploading && <p><Spinner animation="border" size="sm" /> Uploading source files...</p>}
              </Card.Body>
        </Card>
        <Card><Card.Body className="ai-assisted-course-authoring-tab__card-body">
          <h4>Reference an existing EdX unit</h4><p>Paste one learner-view URL to improve or reformat its content.</p>
          <div className="ai-assisted-course-authoring-tab__search-control"><Form.Control value={edxUrl} disabled={Boolean(primarySource)} placeholder="https://learning.../block-v1:...+type@vertical+block@..." onChange={(event: React.ChangeEvent<HTMLInputElement>) => { setEdxUrl(event.target.value); setSourceError(null); }} /><Button variant="outline-primary" disabled={!edxUrl.trim() || Boolean(primarySource)} onClick={addEdxUrl}>Add</Button></div>
              </Card.Body>
        </Card>
        {(sources.length > 0 || uploadedSources.length > 0) && (
        <Card><Card.Body className="ai-assisted-course-authoring-tab__card-body">
          <h4>Added sources</h4><p>Source context: about {sourceWords.toLocaleString()} words</p>
          <ul className="ai-assisted-course-authoring-tab__source-list">
            {uploadedSources.map(source => <li key={source.sourceId}><span>{source.name}</span><Badge variant={sourceStatusVariant(source.status)}>{source.status}</Badge></li>)}
            {sources.map(source => <li key={source.id}><span>{source.label}</span><span><Badge variant={source.type === 'web' ? 'info' : 'primary'}>{source.type === 'web' ? 'Web' : 'EdX'}</Badge> <Button variant="tertiary" size="sm" onClick={() => setSources(current => current.filter(item => item.id !== source.id))}>Remove</Button></span></li>)}
          </ul>
          {primarySource && (
          <Form.Switch id="ai-unit-full-context" checked={fullContextMode} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFullContextMode(event.target.checked)}>
            Full Context Mode: use up to {FULL_CONTEXT_CHUNK_CAP} chunks from the referenced unit and disable additional source search.
          </Form.Switch>
          )}
        </Card.Body>
        </Card>
        )}
        {sourceError && <Alert variant="danger">{sourceError}</Alert>}
        <Card className={searchDisabled ? 'ai-assisted-course-authoring-tab__source-search--disabled' : ''}><Card.Body className="ai-assisted-course-authoring-tab__card-body">
          <div className="ai-assisted-course-authoring-tab__skill-results-heading"><div><h4>Find additional sources</h4><p>Search the shared EdX library and web, then choose the sources to include.</p></div><Button disabled={!topic.trim() || searchDisabled || isSearching} onClick={() => searchSources()}>{isSearching ? 'Searching...' : 'Search'}</Button></div>
          <Form.Checkbox checked={useEdx} disabled={searchDisabled} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setUseEdx(event.target.checked)}>EdX Library</Form.Checkbox>
          <Form.Checkbox checked={useWeb} disabled={searchDisabled} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setUseWeb(event.target.checked)}>Web via Tavily</Form.Checkbox>
          {searchResults?.length === 0 && <Alert variant="info">No sources found. Broaden the topic or enable both searches.</Alert>}
          {searchResults && searchResults.length > 0 && (
          <><div className="ai-assisted-course-authoring-tab__skill-grid">{searchResults.map(source => {
            const added = sources.some(item => item.id === resultId(source));
            return <Card key={source.id} className={`ai-assisted-course-authoring-tab__source-result${added ? ' ai-assisted-course-authoring-tab__source-result--selected' : ''}`}><Card.Body className="ai-assisted-course-authoring-tab__card-body"><Badge variant={source.type === 'web' ? 'info' : 'primary'}>{source.type === 'web' ? 'Web' : 'EdX'}</Badge>{source.score !== undefined && <Badge variant="light">{Math.round(source.score * 100)}%</Badge>}<h5>{source.title}</h5><p>{source.snippet}</p>{source.url && <a href={source.url} target="_blank" rel="noreferrer">View source</a>}<Button size="sm" variant={added ? 'outline-primary' : 'primary'} onClick={() => toggleSearchResult(source)}>{added ? 'Remove' : 'Add source'}</Button></Card.Body></Card>;
          })}
            </div><Button variant="outline-primary" disabled={isLoadingMore} onClick={() => searchSources(true)}>{isLoadingMore ? 'Loading...' : 'Find more'}</Button>
          </>
          )}
                                                                                                            </Card.Body>
        </Card>
      </div>
      )}

      {activeStep === 2 && (
      <div className="ai-assisted-course-authoring-tab__template-grid">
        {TEMPLATES.map(item => <Card key={item.id} isClickable role="button" aria-pressed={templateId === item.id} onClick={() => setTemplateId(item.id)} className={`ai-assisted-course-authoring-tab__template${templateId === item.id ? ' ai-assisted-course-authoring-tab__template--selected' : ''}`}><Card.Body className="ai-assisted-course-authoring-tab__card-body"><h4>{item.label}</h4><p><strong>{item.tagline}</strong></p><p>{item.description}</p><div>{item.structure.map(part => <Badge key={part} variant="light">{part}</Badge>)}</div></Card.Body></Card>)}
      </div>
      )}

      {activeStep === 3 && (
      <div className="ai-assisted-course-authoring-tab__unit-step">
        <Card><Card.Body className="ai-assisted-course-authoring-tab__card-body"><h4>Generation summary</h4><p><strong>Topic:</strong> {topic}</p><p><strong>Template:</strong> {template ? `${template.label} - ${template.tagline}` : templateId}</p><p><strong>Sources:</strong> {sources.length + uploadedSources.length || 'No sources; semantic search will be used.'}</p>{instructions.trim() && <p><strong>Instructions:</strong> {instructions}</p>}<Button disabled={sourceProcessing || Boolean(job.job && !job.isTerminal)} onClick={submit}>{generationLabel()}</Button></Card.Body></Card>
        {sourceProcessing && <Alert variant="warning">Wait for source extraction before generating this unit.</Alert>}
        {jobId && <JobStatus job={job.job} error={job.error} onCancel={() => cancelSkillBuilderJob(jobId)} />}
        {result && <Card><Card.Body className="ai-assisted-course-authoring-tab__card-body"><div className="ai-assisted-course-authoring-tab__skill-results-heading"><div><h4>Generated HTML</h4><p>{countHtmlWords(result.html)} words, target {wordCount}</p></div><Button variant="outline-primary" onClick={copyHtml}>{copied ? 'Copied' : 'Copy HTML'}</Button></div>{result.concepts_taught.length > 0 && <p><strong>Concepts taught:</strong> {result.concepts_taught.map(concept => <Badge key={concept} variant="success">{concept}</Badge>)}</p>}{result.sources_cited.length > 0 && <p><strong>Sources cited:</strong> {result.sources_cited.map(source => <Badge key={source} variant="light">{source}</Badge>)}</p>}<pre className="ai-assisted-course-authoring-tab__result-preview">{result.html}</pre><Alert variant="info">Paste this self-contained HTML into an HTML component in EdX Studio.</Alert></Card.Body></Card>}
      </div>
      )}

      <div className="ai-assisted-course-authoring-tab__unit-navigation">
        {activeStep > 0 && <Button variant="outline-secondary" onClick={() => setActiveStep(current => current - 1)}>Back</Button>}
        {activeStep < STEPS.length - 1 && <Button disabled={!canContinue()} onClick={() => setActiveStep(current => current + 1)}>Next: {STEPS[activeStep + 1]}</Button>}
        {activeStep === STEPS.length - 1 && result && <Button variant="outline-primary" onClick={reset}>Start over</Button>}
      </div>
    </section>
  );
};

export default SingleUnitTab;
